import { HttpClient, HttpContext } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@environment/environment';
import { CANDIDATE_ROLES, COMPANY_ROLES } from '@guards/portal-role.guard';
import { SKIP_ERROR_TOAST } from '@interceptors/error-toast.interceptor';
import { ANALYTICS_EVENTS, type ClaimedMilestonesDTO } from '@libs/dtos/merit/analytics.dto';
import { sanitizeAnalyticsLocation, sanitizeAnalyticsPath } from '@libs/utils/analytics-url.util';
import { filter } from 'rxjs';
import { CurrentUserService } from '../auth/current-user.service';

type DataLayerValue = string | number | boolean;
type AccountType = 'company' | 'candidate' | 'staff' | 'anonymous';

/** Where the "load GTM on this non-production host" opt-in survives SPA reloads. */
const DEBUG_FLAG_KEY = 'kairos-gtm-debug';

/**
 * The app's only door to Google Tag Manager and GA4 (21/09).
 *
 * Three jobs, and the reason each lives here and nowhere else:
 *
 * - **Loading the container**, once. The website (kairosmerit.ae) ships the same
 *   GTM-W247XMFM, and web and app share the `.kairosmerit.ae` cookie domain, so
 *   GA4 sees one visitor walking from the landing into the app. A second copy of
 *   the snippet would double every tag; `loadContainer()` looks for one first.
 * - **Page views** on every in-app navigation. This is an SPA: without it GA4
 *   would count the first screen and nothing after. Every URL leaves through
 *   `sanitizeAnalyticsLocation` — several routes carry an e-mail or a one-time
 *   token, and GA4 is a third party.
 * - **The funnel events.** `sign_up` is pushed by the signup screen when the
 *   server confirms the account. The rest come from the server
 *   (`POST /analytics/milestones/claim`), which remembers each one and hands it
 *   over exactly once — reloads, retries or a second device cannot repeat a
 *   conversion. See `AnalyticsMilestonesService`.
 *
 * Pushing to `dataLayer` happens even where the container is not loaded (local,
 * staging without `?gtm_debug`): an array nobody reads costs nothing, and it is
 * what lets the events be checked in the console before GTM is wired.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
	private readonly router = inject(Router);
	private readonly http = inject(HttpClient);
	private readonly currentUser = inject(CurrentUserService);
	private readonly destroyRef = inject(DestroyRef);

	private started = false;

	/** Boot: container (when this environment allows it) and page views. Idempotent. */
	start(): void {
		if (this.started) return;
		this.started = true;

		const gtm = (environment as { gtm?: { containerId: string | null; onlyWhenDebugging: boolean } }).gtm;
		if (gtm?.containerId && (!gtm.onlyWhenDebugging || this.debugRequested())) {
			this.loadContainer(gtm.containerId);
		}

		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => this.pageView());

		// A session restored from storage (a reload, a new tab) may have milestones
		// waiting: the most common one is `account_activated`, which Kairos grants
		// from the backoffice while the company is not on the page.
		this.claimMilestones();
	}

	/**
	 * Pushes one event. Only non-personal values: GA4 is a third party, and a
	 * name, an address or a profile text has no business in it.
	 */
	push(event: string, params: Record<string, DataLayerValue> = {}): void {
		const w = window as unknown as { dataLayer?: unknown[] };
		w.dataLayer = w.dataLayer ?? [];
		w.dataLayer.push({ event, ...params });
	}

	/** `sign_up` — the company account now exists (called on the server's 2xx, never before). */
	companySignedUp(source: string): void {
		this.push(ANALYTICS_EVENTS.signUp, {
			method: 'email',
			account_type: 'company',
			plan: 'freemium',
			form_name: 'Company Signup',
			source,
		});
	}

	/**
	 * Asks the server for the funnel milestones this account has reached and
	 * nobody has delivered yet, and pushes them.
	 *
	 * Company sessions only, and that is load-bearing: a candidate held by the
	 * access rule gets a 401 on any route outside its list, and the HTTP layer
	 * reads a 401 as an expired session. Silent on failure — analytics can never
	 * be the reason a screen breaks.
	 */
	claimMilestones(): void {
		const type = this.accountType();
		// Staff have nothing to claim. Candidates only ever receive `sign_up`;
		// the company milestones stay on company sessions, where they were.
		if (type !== 'company' && type !== 'candidate') return;
		this.http
			.post<ClaimedMilestonesDTO>(
				`${environment.API_URL}/analytics/milestones/claim`,
				{},
				{ context: new HttpContext().set(SKIP_ERROR_TOAST, true) },
			)
			.subscribe({
				next: ({ milestones }) => {
					for (const milestone of milestones) {
						if (milestone.kind === ANALYTICS_EVENTS.signUp) {
							// The candidate payload is exactly what the GTM tag reads.
							// Company `sign_up` is pushed by the signup screen, not here.
							if (type === 'candidate') this.push(ANALYTICS_EVENTS.signUp, { account_type: 'candidate' });
							continue;
						}
						if (type !== 'company') continue;
						this.push(milestone.kind, { account_type: 'company', ...milestone.params });
					}
				},
				error: () => undefined,
			});
	}

	private pageView(): void {
		this.push(ANALYTICS_EVENTS.pageView, {
			page_location: sanitizeAnalyticsLocation(window.location.href),
			page_path: sanitizeAnalyticsPath(window.location.pathname),
			page_title: document.title,
			account_type: this.accountType(),
		});
	}

	/** Which side of the platform is browsing — so GA4 can keep the two funnels apart. */
	private accountType(): AccountType {
		const role = (this.currentUser.getUserRoleName() ?? '').toLowerCase();
		if (!role) return 'anonymous';
		if (COMPANY_ROLES.includes(role)) return 'company';
		if (CANDIDATE_ROLES.includes(role)) return 'candidate';
		return 'staff';
	}

	/**
	 * Tag Assistant opens the page with `?gtm_debug=…`. Remembered for the tab,
	 * because the first redirect of the app (login, a guard) drops the query.
	 */
	private debugRequested(): boolean {
		try {
			if (new URLSearchParams(window.location.search).has('gtm_debug')) {
				sessionStorage.setItem(DEBUG_FLAG_KEY, '1');
			}
			return sessionStorage.getItem(DEBUG_FLAG_KEY) === '1';
		} catch {
			return new URLSearchParams(window.location.search).has('gtm_debug');
		}
	}

	/** The standard GTM snippet, injected once. */
	private loadContainer(containerId: string): void {
		if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${containerId}"]`)) return;
		const w = window as unknown as { dataLayer?: unknown[] };
		w.dataLayer = w.dataLayer ?? [];
		w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
		const script = document.createElement('script');
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
		document.head.appendChild(script);
	}
}
